import { FC, FocusEvent, useEffect, useMemo, useState } from "react";
import { Box, createStyles, makeStyles } from "@material-ui/core";
import { useFormik } from "formik";
import { startCase } from "lodash";
import * as yup from "yup";

import { Button, DataDisclosureMessage } from "components";
import OutlinedTextField, {
  OutlinedTextFieldProps,
} from "components/OutlinedTextField";
import { RoyaltyAccountCredentials } from "services/royaltyAccount/types";
import { RoyaltyPayor } from "services/royaltyPayor/types";
import { textFieldPurpleStyles } from "theme/constants";

const useStyles = makeStyles(() =>
  createStyles({
    inputRoot: textFieldPurpleStyles,
  }),
);

export type FormValues = RoyaltyAccountCredentials & {
  twoFACode: string;
};

export interface FormProps {
  onSubmit: (values: FormValues) => Promise<void>;
  initialValues: FormValues;
  isOnTwoFAStep: boolean;
  passwordProps: Partial<OutlinedTextFieldProps>;
  usernameProps: Partial<OutlinedTextFieldProps>;
  royaltyPayor: RoyaltyPayor;
  showDataDisclosure?: boolean;
  submitButtonProps?: { fullWidth: boolean };
  submitButtonText?: string;
}

const CredentialsForm: FC<FormProps> = ({
  onSubmit,
  initialValues,
  passwordProps,
  usernameProps,
  isOnTwoFAStep,
  showDataDisclosure = true,
  submitButtonProps,
  submitButtonText = "Connect Account",
  royaltyPayor,
}) => {
  const classes = useStyles();
  const [twoFABlurred, setTwoFABlurred] = useState(false);

  const validationSchema = useMemo(() => {
    if (isOnTwoFAStep) {
      return yup.object().shape({
        twoFACode: yup.string().required("2FA code is required"),
      });
    }

    return yup.object().shape({
      username: yup
        .string()
        .max(128)
        .when([], () => {
          return royaltyPayor.name === "ascap"
            ? yup.string().matches(/^[^\s]+$/, {
                message: "Username cannot contain spaces",
                excludeEmptyString: true,
              })
            : yup.string();
        })
        .label(startCase(royaltyPayor.username_or_email ?? "username"))
        .required("Required"),
      // using a custom `test` here to create a small delay when the user blurs the field: prevents the error message from flashing if the user selects "Remove Royalty Account"
      password: yup
        .string()
        .test("required", "Required", async function (value) {
          await new Promise(resolve => setTimeout(resolve, 60));
          return !!value;
        }),
    });
  }, [isOnTwoFAStep, royaltyPayor]);

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: async values => {
      setTwoFABlurred(true); // Show errors on submit attempt
      await onSubmit(values);
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
  } = formik;

  const handleTwoFABlur = (e: FocusEvent) => {
    setTwoFABlurred(true);
    handleBlur(e);
  };

  useEffect(() => {
    setTwoFABlurred(false);
  }, [isOnTwoFAStep]);

  const shouldShowTwoFAError =
    twoFABlurred || (values.twoFACode && values.twoFACode.length === 6);

  return (
    <form>
      {isOnTwoFAStep ? (
        <Box my={2} textAlign="left">
          <OutlinedTextField
            autoFocus
            className={classes.inputRoot}
            data-attr="connect-royalty-payor-two-fa-code"
            label="Code"
            name="twoFACode"
            value={values.twoFACode || ""}
            onChange={e => {
              e.target.value = e.target.value.trim();
              handleChange(e);
            }}
            onBlur={handleTwoFABlur}
            InputLabelProps={{ shrink: !!values.twoFACode }}
            error={!!errors.twoFACode && shouldShowTwoFAError}
            FormHelperTextProps={{
              error: !!errors.twoFACode && shouldShowTwoFAError,
            }}
            helperText={
              shouldShowTwoFAError && !!errors.twoFACode && errors.twoFACode
            }
            placeholder="Enter your two-factor authentication code"
          />
        </Box>
      ) : (
        <>
          <Box my={2} textAlign="left">
            <OutlinedTextField
              autoFocus
              data-attr="connect-royalty-payor-username"
              className={classes.inputRoot}
              label={startCase(royaltyPayor.username_or_email ?? "username")}
              name="username"
              value={values.username}
              onChange={handleChange}
              onBlur={handleBlur}
              InputLabelProps={{ shrink: !!values.username }}
              error={!!errors.username && !!touched.username}
              FormHelperTextProps={{
                error: !!errors.username,
              }}
              helperText={
                !!touched.username && !!errors.username && errors.username
              }
              {...(usernameProps ?? {})}
            />
          </Box>
          <Box my={2} textAlign="left">
            <OutlinedTextField
              className={classes.inputRoot}
              data-attr="connect-royalty-payor-password"
              label="Password"
              name="password"
              type="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              InputLabelProps={{ shrink: !!values.password }}
              error={!!errors.password && !!touched.password}
              FormHelperTextProps={{
                error: !!errors.password,
              }}
              helperText={
                !!touched.password && !!errors.password && errors.password
              }
              {...(passwordProps ?? {})}
            />
            {showDataDisclosure && (
              <Box mt={1}>
                <DataDisclosureMessage />
              </Box>
            )}
          </Box>
        </>
      )}
      <Box display="flex" flexDirection="row-reverse" width="100%">
        <Button
          fullWidth={submitButtonProps?.fullWidth}
          style={{
            width: submitButtonProps?.fullWidth ? undefined : "175px",
          }}
          customColor="green"
          variant="contained"
          disabled={isSubmitting || !isValid}
          type="submit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          data-attr="connect-royalty-payor-submit-button"
          data-ph-capture-attribute-is-credentials={
            isOnTwoFAStep ? "false" : "true"
          }
          data-ph-capture-attribute-is-two-fa={isOnTwoFAStep ? "true" : "false"}
        >
          {!isSubmitting
            ? isOnTwoFAStep
              ? "Verify Code"
              : submitButtonText
            : "Verifying"}
        </Button>
      </Box>
    </form>
  );
};

export default CredentialsForm;
